import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { projectsAPI, visitsAPI, usersAPI } from '../services/api';
import './Form.css';

function SiteVisitForm() {
  const navigate = useNavigate();
  const { projectId, visitId } = useParams();
  const isEdit = Boolean(visitId);

  const [project, setProject] = useState(null);
  const [assessors, setAssessors] = useState([]);
  const [formData, setFormData] = useState({
    project_id: projectId,
    visit_date: '',
    construction_status: '',
    project_stage: '',
    assessor_ids: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchProject();
    fetchAssessors();
    if (isEdit) {
      fetchVisit();
    }
  }, [projectId, visitId]);

  const fetchProject = async () => {
    try {
      const response = await projectsAPI.getById(projectId);
      setProject(response.data.data);
    } catch (err) {
      console.error('Error fetching project:', err);
    }
  };

  const fetchAssessors = async () => {
    try {
      const response = await usersAPI.getAssessors();
      setAssessors(response.data.data);
    } catch (err) {
      console.error('Error fetching assessors:', err);
    }
  };

  const fetchVisit = async () => {
    try {
      setLoading(true);
      const response = await visitsAPI.getById(visitId);
      const visit = response.data.data;
      setFormData({
        project_id: visit.project_id,
        visit_date: visit.visit_date.split('T')[0],
        construction_status: visit.construction_status || '',
        project_stage: visit.project_stage || '',
        assessor_ids: visit.assessors?.map((a) => a.user_id) || [],
      });
    } catch (err) {
      setError('Failed to fetch visit details');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleAssessorChange = (e) => {
    const value = parseInt(e.target.value);
    const checked = e.target.checked;

    setFormData((prev) => ({
      ...prev,
      assessor_ids: checked
        ? [...prev.assessor_ids, value]
        : prev.assessor_ids.filter((id) => id !== value),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await visitsAPI.update(visitId, formData);
        navigate(`/visits/${visitId}`);
      } else {
        const response = await visitsAPI.create(formData);
        const newVisitId = response.data.data.visit_id;
        navigate(`/visits/${newVisitId}/assess`);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save site visit');
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!project) return <div className="loading">Loading...</div>;

  return (
    <div className="form-container">
      <h2>{isEdit ? 'Edit Site Visit' : 'New Site Visit'}</h2>
      
      <div className="project-info-box">
        <h3>{project.project_name}</h3>
        <p>
          <strong>Project Code:</strong> {project.project_code}
        </p>
        <p>
          <strong>Location:</strong> {project.location}
        </p>
      </div>

      {error && <div className="error-message">{error}</div>}

      <form onSubmit={handleSubmit} className="form">
        <div className="form-group">
          <label htmlFor="visit_date">Date of Site Visit *</label>
          <input
            type="date"
            id="visit_date"
            name="visit_date"
            value={formData.visit_date}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="project_stage">Project Stage</label>
          <select
            id="project_stage"
            name="project_stage"
            value={formData.project_stage}
            onChange={handleChange}
          >
            <option value="">Select stage</option>
            <option value="Foundation work in progress">Foundation work in progress</option>
            <option value="Structural work in progress">Structural work in progress</option>
            <option value="Finishing work in progress">Finishing work in progress</option>
            <option value="Near completion">Near completion</option>
            <option value="Completed">Completed</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="construction_status">Construction Status</label>
          <textarea
            id="construction_status"
            name="construction_status"
            value={formData.construction_status}
            onChange={handleChange}
            rows="4"
            placeholder="Describe the current construction status..."
          />
        </div>

        <div className="form-group">
          <label>GRIHA Representatives</label>
          <div className="checkbox-group">
            {assessors.map((assessor) => (
              <label key={assessor.user_id} className="checkbox-label">
                <input
                  type="checkbox"
                  value={assessor.user_id}
                  checked={formData.assessor_ids.includes(assessor.user_id)}
                  onChange={handleAssessorChange}
                />
                {assessor.full_name}
              </label>
            ))}
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Saving...' : isEdit ? 'Update Visit' : 'Create & Start Assessment'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default SiteVisitForm;
